import { Chat, ChatContent } from '@glade-ai/chat-plugin';
import { Card, Field, FieldLabel, Switch, cn } from '@glade-ai/ui';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import { nanoid } from 'nanoid';
import { useRouter } from 'next/router';
import { type FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { type Descendant } from 'slate';

import BackButton from '@/components/BackButton';
import Buttons from '@/components/Buttons';
import CustomLink from '@/components/CustomLink';
import HelpCenterCard from '@/components/HelpCenterCard';
import ArrowDown from '@/components/Icons/ArrowDown';
import ArrowUp from '@/components/Icons/ArrowUp';
import CaretCircleDown from '@/components/Icons/CaretCircleDown';
import CaretRight from '@/components/Icons/CaretRight';
import NoodleAI from '@/components/Icons/NoodleAI';
import Modal from '@/components/Modal';
import PaginationObserver from '@/components/PaginationObserver';
import ProductIcon from '@/components/ProductIcon';
import ProgressIndicator from '@/components/ProgressIndicator';
import ShortcutKeys from '@/components/ShortcutKeys';
import Spacer from '@/components/Spacer';
import UserImage from '@/components/UserImage';
import { GLADE_SUPPORT_URL, NOODLE_CREATOR_PERSON_ID, YOUR_AI_CREATOR_PERSON_ID } from '@/configuration/client';
import { ConversationComposeBox } from '@/containers/chat-composeboxes/ConversationComposeBox';
import { ConversationMessage } from '@/containers/chat-messages/ConversationMessage';
import { withTimezone } from '@/format/datetimeLocalTZ';
import deserializeQueryStringItem from '@/helpers/deserializeQueryStringItem';
import isInIframe from '@/helpers/isInIframe';
import isInPopoverWidget from '@/helpers/isInPopoverWidget';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import useNoodleApi from '@/hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import ConversationThreadModal from '@/modals/ConversationThreadModal';
import { useUser } from '@/providers/Auth';
import useHistory from '@/providers/History';
import { JobType, useJobContext } from '@/providers/Jobs';
import JobsContainer from '@/providers/Jobs/JobsContainer';
import updateFulfillment from '@/tsClient/conversations/updateFulfillment';
import type * as ApiModels from '@/typings/api-models';
import { NoodleProductTypes } from '@/typings/graphql-models';

import s from './ChatWithExpert.module.scss';
import { type ChatMessage, type ConversationChatProps } from './types';

const FREE_CHAT_SIGN_UP_URL = 'start-chatting';
const SCROLL_DISTANCE_CONSIDERED_TO_BE_BOTTOM = 10;

/** Full chat interface with message list, compose box, and AI assistant controls. */
export const ConversationChat: FC<ConversationChatProps> = ({
  conversationSid,
  currentPage,
  messageData,
  isTheCreator,
  creator,
  customer,
  refetchPurchases,
  rootMessageId,
  onSendMessage,
  editMessageFn,
  onDeleteMessage,
  onFetchNextPage,
  isAllMessages,
  isPostingComment,
  purchases,
  isAITyping,
  isAIEnabled: isAIEnabledOnConversation,
  setAIEnabled,
  isEmbedded,
  isEmbeddedFromModal,
  scrollRootEl,
  isPaginationEnabled,
  isInitialFetch,
  isAnonymousChat,
  reloadConversation,
  onThreadReply,
  gladeConversationSid,
  isCreatorView = false,
}: ConversationChatProps) => {
  const router = useRouter();
  const [isMarkingAsDone, setIsMarkingAsDone] = useState<string>('');
  const allMessagesRef = useRef(isAllMessages);
  const [jobCorrelationId, setJobCorrelationId] = useState(nanoid());
  const [isAsync, setIsAsync] = useState(false);
  const [isFromSignUpFlow, setIsFromSignUpFlow] = useState(false);
  // state version is for iframe/"load more" button, ref is to stop handleFetchNextPage from firing again.
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const isFetchingNextPageRef = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialThreadId = deserializeQueryStringItem(router.query.threadId, { type: 'string' });
  const [isThreadModalOpen, setIsThreadModalOpen] = useState(Boolean(initialThreadId));
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(initialThreadId || null);
  const [user] = useUser();
  const mostRecentMessageRef = useRef<string | null>(null);
  const desiredScrollFromBottom = useRef<number | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const newMessageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [areNewMessagesVisible, setAreNewMessagesVisible] = useState(true);
  const areNewMessagesVisibleRef = useRef(true);
  const [areUnreadMessagesAbove, setAreUnreadMessagesAbove] = useState(true);
  const justScrolledToMessageRef = useRef(false);
  const SCROLL_ADJUSTMENT_DELAY = 100;
  const SMOOTH_SCROLL_DURATION = 600;
  const isAiPreview = creator?.personId === YOUR_AI_CREATOR_PERSON_ID;
  const isCreatorSuccess = creator?.personId === NOODLE_CREATOR_PERSON_ID;

  const { jobs, addJob } = useJobContext();
  const { history } = useHistory();
  const uploadJobs = jobs.filter((j) => j.correlationId === jobCorrelationId);

  const { getData: markPurchaseAsDone } = useNoodleApi(updateFulfillment);

  const [currentMessage, setCurrentMessage] = useState<ChatMessage | null | undefined>(null);

  // Set the desiredScrollFromBottom to 0 here so make sure it's before the resize observer fires.
  const newMostRecentMessageId = messageData?.[0]?.id;
  if (newMostRecentMessageId && newMostRecentMessageId !== mostRecentMessageRef.current) {
    desiredScrollFromBottom.current = 0;
  }
  mostRecentMessageRef.current = newMostRecentMessageId ?? null;

  const { newChatMessageIds, setNewChatMessageIds, setLastViewedGladeConversation } = useContext(DashboardContext);

  // Save last viewed timestamp when Glade conversation is opened/viewed
  useEffect(() => {
    if (conversationSid && conversationSid === gladeConversationSid && messageData.length > 0) {
      setLastViewedGladeConversation({
        conversationSid,
        timestamp: new Date().toISOString(),
      });
    }
  }, [conversationSid, gladeConversationSid, messageData.length, setLastViewedGladeConversation]);

  const handleAsyncSend = async ({
    attachments,
    text,
    handbooks = [],
    medias = [],
  }: {
    text?: { children: Descendant[] }[];
    handbooks?: { id: string }[];
    attachments?: ApiModels.CreateMessageAttachment[];
    medias?: { id: string }[];
  }): Promise<void> => {
    if (currentMessage && editMessageFn) {
      const sendFunction = async (overrideMediaIds?: { id: string }[]): Promise<void> => {
        await editMessageFn({
          attachments,
          handbooks,
          id: currentMessage.id,
          medias: [...medias, ...(overrideMediaIds || [])],
          text,
        });
      };
      addJob({
        dependsOn: uploadJobs,
        function: sendFunction,
        metadata: {
          replyingTo: rootMessageId,
        },
        title: 'Chat Message',
        type: JobType.CHAT_MESSAGE,
      });
      setCurrentMessage(null);
    } else {
      const sendFunction = async (overrideMediaIds?: { id: string }[]): Promise<void> => {
        await onSendMessage({
          attachments,
          handbooks,
          medias: [...medias, ...(overrideMediaIds || [])],
          text,
        });
      };
      addJob({
        dependsOn: uploadJobs,
        function: sendFunction,
        metadata: {
          replyingTo: rootMessageId,
        },
        title: 'Chat Message',
        type: JobType.CHAT_MESSAGE,
      });
    }
  };

  const handleSend = async ({
    attachments,
    text,
    handbooks = [],
    medias = [],
  }: {
    text?: { children: Descendant[] }[];
    handbooks?: { id: string }[];
    attachments?: ApiModels.CreateMessageAttachment[];
    medias?: { id: string }[];
  }): Promise<void> => {
    if (isAsync) {
      await handleAsyncSend({ attachments, handbooks, medias, text });
    } else if (currentMessage && editMessageFn) {
      await editMessageFn({
        attachments,
        handbooks,
        id: currentMessage.id,
        medias,
        text,
      });
    } else {
      await onSendMessage({
        attachments,
        handbooks,
        medias,
        text,
      });
    }
    setIsAsync(false);
    setJobCorrelationId(nanoid());
  };

  const handleFileChange = (files: unknown[]): void => {
    if (files.length > 0) {
      setIsAsync(true);
    } else {
      setIsAsync(false);
    }
  };

  const handleMediaChange = (medias: unknown[]): void => {
    setIsAsync(medias.length > 0);
  };

  const handleFetchNextPage = async (): Promise<void> => {
    if (isFetchingNextPageRef.current) {
      return;
    }

    if (rootMessageId) {
      isFetchingNextPageRef.current = true;
      setIsFetchingNextPage(true);

      // Store the current scroll position before loading
      const scrollHeightBefore = scrollRootEl ? scrollRootEl.scrollHeight : document.body.scrollHeight;
      const containerEl = scrollRootEl ?? null;

      const allMessagesFetched = await onFetchNextPage?.(rootMessageId, currentPage, messageData);
      allMessagesRef.current = allMessagesFetched;

      // After loading, adjust scroll to maintain position
      if (containerEl) {
        requestAnimationFrame(() => {
          const scrollHeightAfter = containerEl.scrollHeight;
          const heightDifference = scrollHeightAfter - scrollHeightBefore;
          containerEl.scrollTop += heightDifference;
        });
      }

      isFetchingNextPageRef.current = false;
      setIsFetchingNextPage(false);
    }
  };

  const markAsDone = async (id: string): Promise<void> => {
    setIsMarkingAsDone(id);
    if (conversationSid) {
      await markPurchaseAsDone({ conversationSid, isFulfilled: true, purchaseId: id });
      await refetchPurchases();
    }
    setIsMarkingAsDone('');
  };

  useEffect(() => {
    allMessagesRef.current = isAllMessages;
  }, [isAllMessages]);

  // Keep this scroll listener that handles both contexts
  useEffect(() => {
    const scrollContainer = scrollRootEl || window;
    const containerEl = scrollRootEl ?? null;

    const scrollListener = (): void => {
      if (containerEl) {
        // Modal context: measure from container
        const fromBottom = containerEl.scrollHeight - containerEl.scrollTop - containerEl.clientHeight;
        desiredScrollFromBottom.current = fromBottom < SCROLL_DISTANCE_CONSIDERED_TO_BE_BOTTOM ? 0 : fromBottom;
      } else {
        // Dashboard context: use window (original logic)
        const fromBottom = document.body.scrollHeight - window.scrollY;
        desiredScrollFromBottom.current =
          Math.abs(window.innerHeight - fromBottom) < SCROLL_DISTANCE_CONSIDERED_TO_BE_BOTTOM ? 0 : fromBottom;
      }
    };

    scrollContainer.addEventListener('scroll', scrollListener);
    return () => scrollContainer.removeEventListener('scroll', scrollListener);
  }, [scrollRootEl]);

  useEffect(() => {
    // TODO(will): These are very unreliable signals for determining which elements to use for this feature.
    //  Furthermore, it’s bad practice to pass elements as props. This should either compose refs, select an element
    //  with `.closest` or `.offsetParent`, or some other more deterministic method. Logged as DEV-
    if ((!isInIframe() || isInPopoverWidget()) && ResizeObserver) {
      const targetElement = scrollRootEl || document.body;
      const containerEl = scrollRootEl ?? null;

      const resizeObserver = new ResizeObserver(() => {
        if (desiredScrollFromBottom.current === 0) {
          if (containerEl) {
            // Modal: scroll to bottom
            containerEl.scrollTop = containerEl.scrollHeight;
          } else {
            // Dashboard: use original scrollIntoView
            scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'nearest' });
          }
        } else if (desiredScrollFromBottom.current !== null) {
          if (containerEl) {
            // Modal: maintain position
            const newScroll = containerEl.scrollHeight - containerEl.clientHeight - desiredScrollFromBottom.current;
            containerEl.scrollTop = Math.max(0, newScroll);
          } else {
            // Dashboard: use original window scroll
            const newScroll = document.body.scrollHeight - desiredScrollFromBottom.current;
            window.scrollTo(0, newScroll);
          }
        }
      });

      resizeObserver.observe(targetElement);
      return () => {
        resizeObserver.unobserve(targetElement);
      };
    }
    return () => {};
  }, [scrollRootEl]);

  /**
   * Auto-scroll to bottom when user sends a message (modal-specific fix)
   * Why this is needed: In the modal, ResizeObserver doesn't fire when new messages are added because
   * the container dimensions (height/width) don't change - only the internal scrollHeight changes.
   * ResizeObserver only detects dimension changes, not content changes.
   * This effect explicitly handles the "user sent a message" scenario by watching for state changes and scrolling to bottom.
   * Dashboard doesn't need this because it observes document.body which does resize.
   * */
  useEffect(() => {
    // Only trigger if we just sent a message (posting comment)
    if (!isPostingComment && mostRecentMessageRef.current) {
      const isUserMessage = messageData[0]?.owner?.id === user?.id;

      if (isUserMessage && desiredScrollFromBottom.current === 0) {
        // User just sent a message - scroll to bottom
        requestAnimationFrame(() => {
          const containerEl = scrollRootEl ?? null;
          if (containerEl) {
            containerEl.scrollTop = containerEl.scrollHeight;
          }
        });
      }
    }
  }, [isPostingComment, messageData[0]?.id, scrollRootEl, user?.id]);

  useEffect(() => {
    if (history.length > 1) {
      const lastUrlVisited = history.slice(-2)[0];
      setIsFromSignUpFlow(lastUrlVisited.includes(FREE_CHAT_SIGN_UP_URL));
    }
  }, [history]);

  // Get the first unread message ID (visually topmost due to column-reverse)
  const getFirstUnreadMessageId = useCallback((): string | undefined => {
    for (let i = messageData.length - 1; i >= 0; i -= 1) {
      if (newChatMessageIds.includes(messageData[i].id)) {
        return messageData[i].id;
      }
    }
    return undefined;
  }, [messageData, newChatMessageIds]);

  // Check if the first unread message is currently visible in the viewport
  const checkFirstUnreadMessageVisibility = useCallback((): boolean => {
    const firstUnreadMessageId = getFirstUnreadMessageId();
    if (!firstUnreadMessageId) {
      return true;
    }

    const messageElement = newMessageRefs.current.get(firstUnreadMessageId);
    if (!messageElement) {
      return false;
    }

    const containerEl = scrollRootEl ?? null;
    const rect = messageElement.getBoundingClientRect();

    if (containerEl) {
      // Modal context: check position relative to container viewport
      const containerRect = containerEl.getBoundingClientRect();
      // Message is visible if any part of it is within the container's viewport
      return rect.bottom > containerRect.top && rect.top < containerRect.bottom;
    }
    // Dashboard context: check position relative to window viewport
    // Message is visible if any part of it is within the window viewport
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }, [getFirstUnreadMessageId, scrollRootEl]);

  // Check the position of the first unread message relative to viewport
  const checkMessagePosition = useCallback((): void => {
    const firstUnreadMessageId = getFirstUnreadMessageId();
    if (!firstUnreadMessageId) {
      return;
    }

    const messageElement = newMessageRefs.current.get(firstUnreadMessageId);
    if (!messageElement) {
      return;
    }

    const containerEl = scrollRootEl ?? null;
    const rect = messageElement.getBoundingClientRect();

    if (containerEl) {
      // Modal context: check position relative to container viewport
      const containerRect = containerEl.getBoundingClientRect();
      // If element's top is above the container's top, user needs to scroll up
      // If element's bottom is below the container's bottom, user needs to scroll down
      if (rect.top < containerRect.top) {
        setAreUnreadMessagesAbove(true);
      } else if (rect.bottom > containerRect.bottom) {
        setAreUnreadMessagesAbove(false);
      }
    } else if (rect.top < 0) {
      // Dashboard context: check position relative to window viewport
      // If element is above viewport, user needs to scroll up
      setAreUnreadMessagesAbove(true);
    } else if (rect.bottom > window.innerHeight) {
      // If element is below viewport, user needs to scroll down
      setAreUnreadMessagesAbove(false);
    }
  }, [getFirstUnreadMessageId, scrollRootEl]);

  // Handle intersection observer callback for detecting visible messages
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]): void => {
      const visibleNewMessageIds = new Set<string>();
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const messageId = entry.target.getAttribute('data-message-id');
          if (messageId && newChatMessageIds.includes(messageId)) {
            visibleNewMessageIds.add(messageId);
          }
        }
      });

      // Check if the first unread message is visible
      // First check if it's in the intersection observer entries
      const firstUnreadMessageId = getFirstUnreadMessageId();
      let isFirstUnreadMessageVisible = firstUnreadMessageId ? visibleNewMessageIds.has(firstUnreadMessageId) : true;

      // If not found in entries, check directly (in case intersection observer hasn't fired for it yet)
      if (!isFirstUnreadMessageVisible && firstUnreadMessageId) {
        isFirstUnreadMessageVisible = checkFirstUnreadMessageVisibility();
      }

      areNewMessagesVisibleRef.current = isFirstUnreadMessageVisible;
      setAreNewMessagesVisible(isFirstUnreadMessageVisible);

      // Check position of unread messages if first unread message is not visible
      if (!isFirstUnreadMessageVisible) {
        checkMessagePosition();
      }
    },
    [newChatMessageIds, checkMessagePosition, getFirstUnreadMessageId, checkFirstUnreadMessageVisibility],
  );

  // Handle scroll events to update arrow direction and visibility
  const handleScroll = useCallback((): void => {
    if (!areNewMessagesVisibleRef.current) {
      // Check if the first unread message is now visible
      const isVisible = checkFirstUnreadMessageVisibility();
      if (isVisible) {
        areNewMessagesVisibleRef.current = true;
        setAreNewMessagesVisible(true);
      } else {
        // Update arrow direction if still not visible
        checkMessagePosition();
      }
    }
  }, [checkMessagePosition, checkFirstUnreadMessageVisibility]);

  // Detect if new messages are visible in viewport and their position
  useEffect(() => {
    if (newChatMessageIds.length === 0) {
      areNewMessagesVisibleRef.current = true;
      setAreNewMessagesVisible(true);
      return () => {};
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: scrollRootEl || null,
      rootMargin: '0px',
      threshold: 0.1,
    });

    // Observe all new messages
    newChatMessageIds.forEach((messageId) => {
      const messageElement = newMessageRefs.current.get(messageId);
      if (messageElement) {
        observer.observe(messageElement);
      }
    });

    // Also check position on scroll
    const scrollContainer = scrollRootEl || window;
    scrollContainer.addEventListener('scroll', handleScroll);

    // Initial check
    checkMessagePosition();

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [newChatMessageIds, scrollRootEl, messageData, handleIntersection, handleScroll, checkMessagePosition]);

  // Scroll to first new message
  const scrollToFirstNewMessage = (): void => {
    if (newChatMessageIds.length === 0) {
      return;
    }

    // Set flag to prevent hover from clearing messages immediately after scroll
    justScrolledToMessageRef.current = true;

    // Since mainContent uses column-reverse, the visually "first" (top) message
    // is the last one in the messageData array
    // Find the last message in messageData that has a newChatMessageId
    let firstNewMessage: ChatMessage | undefined;
    for (let i = messageData.length - 1; i >= 0; i -= 1) {
      if (newChatMessageIds.includes(messageData[i].id)) {
        firstNewMessage = messageData[i];
        break;
      }
    }

    if (firstNewMessage) {
      const messageElement = newMessageRefs.current.get(firstNewMessage.id);
      if (messageElement) {
        const containerEl = scrollRootEl ?? null;
        if (containerEl) {
          // For modal context, scroll within the container
          // Use scrollIntoView first, then adjust
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });

          // Additional adjustment after scroll to add more padding
          setTimeout(() => {
            const containerRect = containerEl.getBoundingClientRect();
            const elementRect = messageElement.getBoundingClientRect();
            const scrollPadding = 120; // Increased padding from top of viewport
            const currentScrollTop = containerEl.scrollTop;
            const elementTopRelativeToContainer = elementRect.top - containerRect.top + currentScrollTop;
            const targetScrollTop = elementTopRelativeToContainer - scrollPadding;

            containerEl.scrollTo({
              behavior: 'smooth',
              top: Math.max(0, targetScrollTop),
            });

            // Clear the flag after scroll animation completes (smooth scroll takes ~500ms)
            setTimeout(() => {
              justScrolledToMessageRef.current = false;
            }, SMOOTH_SCROLL_DURATION);
          }, SCROLL_ADJUSTMENT_DELAY);
        } else {
          // For dashboard context, calculate position and scroll directly
          // Get element's position relative to document
          const elementRect = messageElement.getBoundingClientRect();
          const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          const scrollPadding = 150; // Padding from top

          // Calculate absolute position: viewport position + current scroll
          const elementAbsoluteTop = elementRect.top + currentScrollY;
          const targetScrollY = elementAbsoluteTop - scrollPadding;

          window.scrollTo({
            behavior: 'smooth',
            top: Math.max(0, targetScrollY),
          });

          // Clear the flag after scroll animation completes (smooth scroll takes ~500ms)
          setTimeout(() => {
            justScrolledToMessageRef.current = false;
          }, SMOOTH_SCROLL_DURATION);
        }
      }
    }
  };

  const creatorColor = messageData.find((m) => m.owner?.id === creator?.personId)?.owner?.primaryColour?.hex;
  const creatorImage = messageData.find((m) => m.owner?.id === creator?.personId)?.owner?.image;

  // Get the first unread message ID once for use in rendering
  const firstUnreadMessageId = useMemo(() => getFirstUnreadMessageId(), [getFirstUnreadMessageId]);

  const customerProducts: Array<{
    bookingTime?: string;
    id: string;
    name?: string | null;
    timezone?: string | null;
    type?: NoodleProductTypes | null;
    isSubscription: boolean;
  }> = useMemo(
    () => [
      ...(purchases?.personProducts?.map((p) => ({
        bookingTime: p.booking?.startAt ?? undefined,
        id: p.id,
        isSubscription: false,
        name: p.price?.priceTitle || p.product?.title,
        timezone: p.product?.creator?.timezone,
        type: p.product?.productTypes?.[0]?.noodleProductType,
      })) || []),
      ...(purchases?.personSubscriptions?.map((ps) => ({
        id: ps.id,
        isSubscription: true,
        name: ps.priceConnection?.priceTitle || ps.subscriptionModel?.product?.title,
        type: ps.subscriptionModel?.product?.productTypes?.[0]?.noodleProductType,
      })) || []),
    ],
    [purchases],
  );

  const customerImage = customer?.image;

  const canAddMessage =
    !isTheCreator ||
    (isTheCreator && !creator?.isAiEnabled) ||
    (isTheCreator && creator?.isAiEnabled && !isAIEnabledOnConversation);

  const canToggleAIEnabled =
    user?.id === NOODLE_CREATOR_PERSON_ID ||
    (isTheCreator && creator?.isAiEnabled) || // || (isTheCreator && creator.isAiEnabled && !isPayingCustomer)
    (isTheCreator && !creator?.isAiEnabled && !isAIEnabledOnConversation) ||
    (isTheCreator && !canAddMessage);

  useEffect(() => {
    const wrapper = document.querySelector('#creator-ui-provider-style-wrapper');
    if (wrapper && isAiPreview) {
      wrapper.setAttribute('style', `--color-primary: ${customer?.primaryColour?.hex};`);
    }
  }, [customer?.primaryColour?.hex, isAiPreview]);

  const showCustomerProducts =
    isTheCreator &&
    customerProducts.length > 0 &&
    !(customerProducts.length === 1 && customerProducts.find((i) => i.type === NoodleProductTypes.Handbook));

  return (
    <Chat
      data-testid='chat.conversation'
      className={cn(
        s.container,
        'min-h-0 gl-main-height max-h-dvh !grid-rows-[min-content_1fr_min-content] !px-0 overflow-hidden contain-layout',
      )}
    >
      <div role='none'>
        {isThreadModalOpen && conversationSid && currentThreadId && (
          <ConversationThreadModal
            onClose={() => setIsThreadModalOpen(false)}
            onReply={() => onThreadReply && onThreadReply(currentThreadId)}
            conversationSid={conversationSid}
            messageId={currentThreadId}
            editMessageFn={editMessageFn}
          />
        )}
        <div
          className={s.top}
          {...(showCustomerProducts && {
            style: {
              borderRadius: '0 0 8px 8px',
            },
          })}
        >
          {jobs.length > 0 && <JobsContainer />}
          {(!isEmbedded || isTheCreator) && (
            <div
              className={cn(s.userTop, 'px-2 py-3')}
              {...(showCustomerProducts && {
                style: {
                  borderBottom: 0,
                },
              })}
            >
              <BackButton
                backLink={
                  isFromSignUpFlow && creator ? getUrl(IDENTIFIERS.CREATOR, { creatorSlug: creator.slug }) : undefined
                }
                fallBackLink={
                  isTheCreator || isAiPreview
                    ? getUrl(IDENTIFIERS.DASHBOARD)
                    : getUrl(IDENTIFIERS.CREATOR, { creatorSlug: creator?.slug || '' })
                }
                title=''
              />
              <CustomLink
                className={s.topName}
                to={
                  (!isAiPreview && (isTheCreator ? `/dashboard/members/${customer?.id}` : `/${creator?.slug}`)) ||
                  undefined
                }
              >
                {!isTheCreator && !isAiPreview && (
                  <>
                    <UserImage url={creatorImage?.url} size={24} name={creator?.name} color={creatorColor} />
                    <strong>{creator?.name}</strong>
                    <CaretRight size={12} color='var(--color-gray-75)' />
                  </>
                )}
                {(isTheCreator || isAiPreview) && customer && (
                  <>
                    <UserImage
                      key={`${customer.name}-image`}
                      url={customerImage?.url}
                      size={24}
                      name={customer.name}
                      color={customer.primaryColour?.hex}
                    />
                    <strong style={(isAiPreview && { color: customer.primaryColour?.hex }) || undefined}>
                      {customer.name || 'Anonymous'}
                    </strong>
                  </>
                )}
              </CustomLink>
              <div />
            </div>
          )}
          {showCustomerProducts && (
            <details className={s.showProducts}>
              <summary>
                <span>
                  Purchased products
                  <small>{customerProducts.length}</small>
                </span>
                <CaretCircleDown size={12} weight='fill' color='var(--color-gray-100)' />
              </summary>
              <ul className={s.products}>
                {customerProducts.map((i) => (
                  <li key={i.id}>
                    <p>
                      <ProductIcon size={12} noodleProductType={i.type} />
                      {i.name}
                      {i.bookingTime ? ':' : ''}
                      {i.bookingTime && <span>{withTimezone({ datetime: i.bookingTime, timezone: i.timezone })}</span>}
                    </p>
                    {!i.isSubscription && !i.bookingTime && (
                      <>
                        <Buttons isWrapper onClick={() => setIsModalOpen(true)}>
                          Mark as done
                        </Buttons>
                        {isModalOpen && (
                          <Modal title='Mark as done?' onClose={() => setIsModalOpen(false)}>
                            <div className={s.wrapperModal}>
                              <p className='body-md-bold'>You’re about to end your paid chat with {customer?.name}</p>
                              <p className='body-sm'>
                                They will be notified and asked for a review. They’ll still be able to message you, but
                                their messages will now appear in the ‘Leads’ section of your queue.
                              </p>
                              <Buttons
                                onClick={() => {
                                  markAsDone(i.id);
                                  setIsModalOpen(false);
                                }}
                                isSecondary
                                isFetching={isMarkingAsDone === i.id}
                                isFullWidth
                              >
                                Mark as done
                              </Buttons>
                            </div>
                          </Modal>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {isCreatorSuccess && !isTheCreator && (
            <HelpCenterCard
              onClick={() => {
                window.open(GLADE_SUPPORT_URL, '_blank', 'noopener,noreferrer');
              }}
            />
          )}
        </div>
      </div>
      <ChatContent
        data-testid='chat.messages'
        className={cn(
          s.mainContent,
          'pb-2',
          // TODO(will): Make deterministic, DEV-13204
          (canToggleAIEnabled || isEmbeddedFromModal) && 'pb-[3rem]',
          canToggleAIEnabled && isEmbeddedFromModal && 'pb-[6rem]',
        )}
      >
        <div ref={scrollAnchorRef} />
        {isAITyping && creator && (
          <ConversationMessage
            creatorPersonId={creator.personId || ''}
            creatorSlug={creator.slug}
            canDeleteMessages={false}
            canPinMessages={false}
            canReplyToMessages={false}
            isEmbeddedFromModal={isEmbeddedFromModal}
            message={{
              createdAt: new Date().toISOString(),
              editedAt: null,
              id: 'responding',
              isDeleted: false,
              owner: isAiPreview
                ? {
                    id: customer?.id || '',
                    image: customerImage,
                    name: customer?.name,
                    primaryColour: { hex: customer?.primaryColour?.hex || '' },
                  }
                : {
                    id: creator.personId || '',
                    image: creatorImage,
                    name: creator.name,
                    primaryColour: { hex: creatorColor || '' },
                  },
              text: 'AI Assistant is typing…',
              title: '',
            }}
            isChatMessage={{
              isAutomated: true,
              isResponding: true,
            }}
          />
        )}
        {uniqBy(
          messageData.filter((m) => (m.isAIGenerated ? m.text || m.richText?.html : true)),
          'id',
        ).map(
          (
            message, // this main-content div is styled column-reverse, pagination is actually from top
          ) => {
            const isFirstUnreadMessage = firstUnreadMessageId === message.id;

            return (
              <div
                key={message.id}
                ref={(el) => {
                  if (newChatMessageIds.includes(message.id)) {
                    if (el) {
                      newMessageRefs.current.set(message.id, el);
                    } else {
                      newMessageRefs.current.delete(message.id);
                    }
                  }
                }}
                data-message-id={message.id}
                className={cn(
                  s.message,
                  s['message-embedded'],
                  newChatMessageIds.includes(message.id) && s['message-highlight'],
                  'max-w-(--width-chat-container) w-full mx-auto',
                )}
                onMouseEnter={
                  isFirstUnreadMessage
                    ? () => {
                        // Don't clear messages if we just scrolled to this message
                        if (!justScrolledToMessageRef.current) {
                          setNewChatMessageIds([]);
                        }
                      }
                    : undefined
                }
              >
                <ConversationMessage
                  creatorPersonId={creator?.personId || ''}
                  creatorSlug={creator?.slug || ''}
                  canDeleteMessages={user?.id === message.owner?.id}
                  canEditMessages={user?.id === message.owner?.id}
                  canPinMessages={false}
                  canReplyToMessages={true}
                  usesSecondaryActionReply={true}
                  customerId={customer?.id}
                  reloadConversation={reloadConversation}
                  setCurrentMessage={setCurrentMessage}
                  currentMessage={message}
                  isEmbeddedFromModal={isEmbeddedFromModal}
                  message={
                    isAiPreview
                      ? {
                          ...message,
                          owner: {
                            id: message.owner?.id === creator?.personId ? customer?.id || '' : creator?.personId || '',
                            image: message.owner?.image === creatorImage ? customerImage : creatorImage,
                            name: message.owner?.name === creator?.name ? customer?.name : creator?.name,
                            primaryColour:
                              message.owner?.primaryColour?.hex === creatorColor
                                ? { hex: customer?.primaryColour?.hex || '' }
                                : { hex: creatorColor || '' },
                          },
                          participants: message.participants ?? [],
                        }
                      : message
                  }
                  isChatMessage={{
                    isAutomated: message.isAIGenerated,
                  }}
                  onDeleteMessage={onDeleteMessage}
                  onOpenThread={(id) => {
                    setIsThreadModalOpen(true);
                    setCurrentThreadId(id);
                  }}
                />
              </div>
            );
          },
        )}
        {!allMessagesRef.current && messageData.length > 1 && isPaginationEnabled && (
          <>
            {isInIframe() ? (
              <PaginationObserver
                showSpinner={false}
                intersectTime={1000}
                isDisabled={isFetchingNextPage}
                loadFn={handleFetchNextPage}
              />
            ) : (
              <PaginationObserver
                showSpinner={isFetchingNextPage}
                intersectTime={1000}
                isDisabled={isFetchingNextPage}
                loadFn={handleFetchNextPage}
                rootEl={scrollRootEl || null}
              />
            )}
          </>
        )}
        {messageData.length === 0 && isInitialFetch && (
          <div
            className={classNames(s.messagesLoader, {
              [s.creatorMessagesLoader]: isCreatorView,
            })}
          >
            {isAnonymousChat ? (
              <>
                {Boolean(isCreatorSuccess) && (
                  <>
                    <Spacer size={0} />
                    <NoodleAI />
                  </>
                )}
                {isCreatorSuccess ? 'Have questions?' : 'No messages yet.'}
                <br />
                {isCreatorSuccess
                  ? 'Start a chat with the Glade AI Assistant.'
                  : `Start conversation with ${creator?.name}.`}
              </>
            ) : (
              <ProgressIndicator />
            )}
          </div>
        )}
        {messageData.length === 0 && !isInitialFetch && (
          <div className={s.messagesLoader}>
            <UserImage
              size={48}
              name={creator?.name}
              isActive
              color={creator?.primaryColour?.hex}
              url={creator?.logoCompact?.url || creator?.person?.image}
            />
            No messages yet.
            <br />
            Start conversation with {creator?.name}.
          </div>
        )}
        {newChatMessageIds.length > 0 && !areNewMessagesVisible && (
          <div className={s.scrollToNewMessagesButtonContainer}>
            <Buttons onClick={scrollToFirstNewMessage} className={s.scrollToNewMessagesButton}>
              <span>
                View {newChatMessageIds.length} unread message{newChatMessageIds.length > 1 ? 's' : ''}
              </span>
              {areUnreadMessagesAbove ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            </Buttons>
          </div>
        )}
      </ChatContent>
      <div role='none' className='w-full max-w-(--width-chat-container) mx-auto relative px-2 pb-4'>
        {canToggleAIEnabled && (
          <Card className='flex-row items-center px-3 py-1 mb-1 absolute bottom-full inset-x-2 z-10'>
            <Field>
              <FieldLabel className='grow cursor-pointer'>
                <NoodleAI />
                <span>
                  <span className='font-semibold'>AI Assistant</span>{' '}
                  {isAIEnabledOnConversation
                    ? 'takes care of your responses. Turn off the switch to answer yourself.'
                    : 'is off'}
                </span>
              </FieldLabel>
              <Switch
                checked={!!isAIEnabledOnConversation}
                disabled={false}
                name='isActive'
                onCheckedChange={(nextChecked) => {
                  setAIEnabled(!!nextChecked);
                }}
                data-testid='chat.aiToggle'
              />
            </Field>
          </Card>
        )}
        {canAddMessage && (
          <ConversationComposeBox
            onSend={handleSend}
            isDisabled={!creator}
            placeholder='Type your message'
            isFetching={isPostingComment}
            showProTips={false}
            onChangeFiles={handleFileChange}
            onChangeMedia={handleMediaChange}
            showAddVideo
            showAddFormRequest={isTheCreator}
            showAddDocumentRequest={isTheCreator}
            showAddHandbook={isTheCreator}
            isLoomEnabled={isTheCreator}
            showAddAnyFile={true}
            showAddPaymentLink={isTheCreator}
            isAsync
            jobCorrelationId={jobCorrelationId}
            referenceId={rootMessageId || nanoid()}
            referenceType={'chat-message'}
            currentMessage={currentMessage}
            isCreatorView={isCreatorView}
            isEmbeddedFromModal={isEmbeddedFromModal}
          />
        )}
        {isEmbeddedFromModal && (
          <div className={s.embeddedFromModalFooter}>
            <div className={s.footerClose}>
              <div className={s.shortcutKeyBorder}>
                <ShortcutKeys value='Esc' uppercase={false} />
              </div>{' '}
              Close
            </div>
          </div>
        )}
      </div>
    </Chat>
  );
};
